/**
 * The till: what a point-of-sale integration tells BarOS, in BarOS's terms.
 * See docs/architecture.md D12.
 *
 * Square is the only provider today, but nothing in this file knows that.
 * `packages/api/src/square.ts` turns Square's orders into the `PosSale` below
 * and everything from there on — which spec a line is, what it took off the
 * shelf, what the night took, how many of each drink a week — is arithmetic
 * over these shapes. It lives in core for the usual reason: the sync writes
 * the figures and the tablet shows them, and they must be the same figures.
 *
 * Three rules worth naming.
 *
 * **Money is integer cents and signed.** A refund is a sale with the sign
 * turned over, so a night's takings are a plain sum and nothing has to
 * remember to subtract.
 *
 * **A till line is mapped, never guessed twice.** The first time an item is
 * seen it is matched to a spec by name, exactly, and the match is stored as a
 * `PosItem` marked `auto`. From then on the stored row decides — renaming a
 * spec does not quietly re-point last month's sales. The one exception is a
 * manager asking: re-reading the catalogue re-guesses the rows nobody has
 * decided (`undecided`), so adding the variants a till already sells puts
 * its guesses right.
 *
 * **Stock moves only for what BarOS can account for.** A line with no
 * mapping takes nothing off the shelf; it is counted as unmapped and shown,
 * because an invisible gap in the stock figures is worse than a visible one.
 */
import { slugify } from "./library.js";
import { buildVolumeMl, hasBuild } from "./recipes.js";
import { addDays } from "./time.js";
export const POS_ENVIRONMENTS = ["production", "sandbox"];
export const POS_TARGET_KINDS = ["spec", "ingredient", "ignore"];
/**
 * The stable id of a till item: the provider's catalogue id when the line has
 * one, otherwise its name.
 *
 * An open-price line — "Bar snacks", keyed in on the night — has no catalogue
 * id, and without the name fallback every such line would be a stranger each
 * time. The `name:` prefix keeps the two id spaces from ever colliding.
 */
export function posItemKey(catalogueId, name) {
    if (catalogueId)
        return catalogueId;
    return `name:${slugify(name ?? "")}`;
}
/** Whether two mappings say the same thing. */
export function sameTarget(a, b) {
    if (!a || !b)
        return a === b;
    if (a.kind === "spec" && b.kind === "spec")
        return a.specId === b.specId;
    if (a.kind === "ingredient" && b.kind === "ingredient") {
        return a.ingredientId === b.ingredientId && a.ml === b.ml;
    }
    return a.kind === b.kind;
}
/**
 * What a till calls an item's only variation when nobody renamed it. Square
 * says `Regular`; a line keyed in by hand has none at all.
 */
const PLAIN_VARIATIONS = new Set(["", "regular"]);
/**
 * The spec a till item is, by its item and variation names, or null.
 *
 * Exact on the slug and nothing looser: `H.S.L Special` matches `HSL special`
 * and does not match `H.S.L Special (batch)`. A near-miss mapped wrongly
 * takes the wrong bottles off the shelf every time it sells, silently, and
 * the cost of not matching is one row in a list for a manager to map. Two
 * specs with the same slug match neither, for the same reason. Retired specs
 * are not matched: the till is selling something, and it is not that.
 *
 * The variation decides as much as the item. Square sells a family as one
 * item — `Negroni`, with variations `Negroni`, `Boulevardier`, `Rum`,
 * `Brandy Negroni` — and each variation is its own till item. Only the
 * plain variation (`Regular`, none, or the item's own name) is the spec the
 * item is named after. Any other is looked for as `<variation>`,
 * `<variation> <item>` or `<item> <variation>` — so `Rum` finds `Rum
 * Negroni` — first among that spec's variants and then across the library.
 * A variation that finds nothing is left unmapped rather than given the
 * primary's build: a Boulevardier is not a Negroni, whatever the till's
 * item is called.
 */
export function autoTarget(name, variationName, specs) {
    const live = specs.filter((s) => s.status !== "retired");
    const one = (found) => found.length === 1 ? { kind: "spec", specId: found[0].specId } : null;
    const matching = (pool, slugs) => pool.filter((s) => slugs.has(s.specId) || slugs.has(slugify(s.name)));
    const itemSlug = slugify(name);
    const named = matching(live, new Set([itemSlug]));
    const variation = variationName.trim();
    if (PLAIN_VARIATIONS.has(variation.toLowerCase()) || slugify(variation) === itemSlug) {
        return name.trim() ? one(named) : null;
    }
    const wanted = new Set([variation, `${variation} ${name}`, `${name} ${variation}`].map((n) => slugify(n)));
    const head = named.length === 1 ? named[0] : undefined;
    if (head) {
        const primaryId = head.variantOf || head.specId;
        const family = live.filter((s) => s.specId === primaryId || s.variantOf === primaryId);
        const inFamily = matching(family, wanted);
        if (inFamily.length > 0)
            return one(inFamily);
    }
    return one(matching(live, wanted));
}
/**
 * Whether a till item's mapping is still the sync's guess rather than
 * anybody's decision: written by `actor` (the sync's name for itself) and
 * never saved by a person since. Covers a name match nobody confirmed and an
 * item nothing matched; a person choosing "Not mapped" is a decision.
 */
export function undecided(item, actor) {
    return item.updatedBy === actor;
}
export const NO_TENDERS = { cashCents: 0, cardCents: 0, otherCents: 0 };
// ------------------------------------------------------------------- stock
function round1(n) {
    return Math.round(n * 10) / 10;
}
/**
 * What a sale's lines take off the shelf, per ingredient, in millilitres.
 *
 * A spec line contributes `ml × quantity` for every line of its recipe
 * (what is served); an ingredient line contributes its measure. Dilution — the water an ice cube
 * becomes — is in a build for the ABV sum and is nothing anybody stocks, so
 * it is left out. So is any line the mappings do not resolve, and any spec
 * the library no longer holds: nothing is taken for a drink BarOS cannot
 * cost.
 *
 * This says what the sale *consumed*. Whether a given move is applied is the
 * sync's decision (`appliesToStock`): the venue has to hold a level for the
 * ingredient, and the level must not have been counted since the sale.
 */
export function stockMoves(lines, itemsByKey, specsById, ingredientsById, 
/**
 * The ingredients the venue holds a level for. Given, a house-made
 * ingredient the venue does not hold is opened into what it is made of, so
 * its bottles are taken as they were before batches were counted; a venue
 * starts counting a batch by making one (#161). Absent, every line is
 * taken as written.
 */
heldIngredientIds) {
    const totals = new Map();
    const take = (ingredientId, ml, path = new Set()) => {
        if (!(ml > 0))
            return;
        const ing = ingredientsById.get(ingredientId);
        if (ing?.kind === "dilution")
            return;
        if (heldIngredientIds && !heldIngredientIds.has(ingredientId) && hasBuild(ing) && !path.has(ingredientId)) {
            const volume = buildVolumeMl(ing.build.lines);
            const inner = new Set(path).add(ingredientId);
            for (const part of ing.build.lines)
                take(part.ingredientId, (part.ml / volume) * ml, inner);
            return;
        }
        totals.set(ingredientId, (totals.get(ingredientId) ?? 0) + ml);
    };
    for (const line of lines) {
        if (!(line.quantity > 0))
            continue;
        const target = itemsByKey.get(line.key)?.target;
        if (!target)
            continue;
        if (target.kind === "ingredient") {
            take(target.ingredientId, target.ml * line.quantity);
        }
        else if (target.kind === "spec") {
            // What is served: a sale pours batch, not the bottles the batch was made from.
            for (const build of specsById.get(target.specId)?.lines ?? []) {
                take(build.ingredientId, build.ml * line.quantity);
            }
        }
    }
    return [...totals.entries()]
        .map(([ingredientId, ml]) => ({ ingredientId, ml: round1(ml) }))
        .filter((m) => m.ml > 0)
        .sort((a, b) => a.ingredientId.localeCompare(b.ingredientId));
}
/**
 * Should this sale move this stock level?
 *
 * Two conditions, both about not counting the same bottle twice.
 *
 * The sale must have closed after the venue was linked — earlier sales are
 * backfilled history, from before the stock figures were told about the till.
 *
 * And it must have closed after the level was last *set*. A stocktake is the
 * truth as of the moment it was finalised: it already reflects every drink
 * poured before it, so a sale from ten minutes earlier arriving in the next
 * sync must not be taken off again. `levelSetAt` is the `STOCK#` row's
 * `updatedAt`, which only a count or a manager's adjustment writes — the
 * sync's own decrements deliberately leave it alone.
 */
export function appliesToStock(closedAt, linkedAt, levelSetAt) {
    if (closedAt < linkedAt)
        return false;
    if (levelSetAt && closedAt <= levelSetAt)
        return false;
    return true;
}
/**
 * Roll one night's sales up. `sales` is every stored sale of that service
 * date at the location the venue is linked to.
 */
export function rollupDay(serviceDate, sales, itemsByKey, updatedAt, locationId = "") {
    let orders = 0;
    let refunds = 0;
    let takingsCents = 0;
    let taxCents = 0;
    let tipCents = 0;
    let discountCents = 0;
    let refundCents = 0;
    let cashCents = 0;
    let cardCents = 0;
    let otherCents = 0;
    const units = {};
    const items = new Map();
    let unmappedUnits = 0;
    for (const sale of sales) {
        if (sale.kind === "refund") {
            refunds += 1;
            refundCents += -sale.totalCents;
        }
        else {
            orders += 1;
        }
        takingsCents += sale.totalCents;
        taxCents += sale.taxCents;
        tipCents += sale.tipCents;
        discountCents += sale.discountCents;
        cashCents += sale.tenders.cashCents;
        cardCents += sale.tenders.cardCents;
        otherCents += sale.tenders.otherCents;
        for (const line of sale.lines) {
            const target = itemsByKey.get(line.key)?.target ?? null;
            const specId = target?.kind === "spec" ? target.specId : null;
            if (specId)
                units[specId] = (units[specId] ?? 0) + line.quantity;
            if (!target)
                unmappedUnits += line.quantity;
            const seen = items.get(line.key);
            if (seen) {
                seen.quantity += line.quantity;
                seen.grossCents += line.grossCents;
            }
            else {
                items.set(line.key, {
                    key: line.key,
                    name: line.name,
                    variationName: line.variationName,
                    quantity: line.quantity,
                    grossCents: line.grossCents,
                    specId,
                    mapped: target !== null,
                });
            }
        }
    }
    return {
        serviceDate,
        locationId,
        saleCount: sales.length,
        orders,
        refunds,
        takingsCents,
        taxCents,
        tipCents,
        discountCents,
        refundCents,
        cashCents,
        cardCents,
        otherCents,
        units,
        items: [...items.values()].sort((a, b) => b.quantity - a.quantity || a.name.localeCompare(b.name)),
        unmappedUnits,
        updatedAt,
    };
}
/**
 * The three POS figures a cash-up asks for, off the night's rollup.
 *
 * This is the half of screen 13 somebody used to read off the till and type
 * in. Floored at zero: a night that refunded more cash than it took is real,
 * and the cash-up's fields are amounts rung up, not a signed ledger — the
 * shortfall shows up where it should, in the drawer count.
 */
export function cashupFiguresFromPos(day) {
    const floor = (n) => Math.max(0, Math.round(n));
    return {
        posCashCents: floor(day.cashCents),
        posCardCents: floor(day.cardCents),
        posOtherCents: floor(day.otherCents),
    };
}
// ------------------------------------------------------------ units per week
/** How far back the units-per-week figure looks. Four of every weeknight. */
export const POS_UNITS_WINDOW_DAYS = 28;
/**
 * Units per week for every spec the till has sold, over the trailing window
 * ending at `through` — what screen 07 plots popularity from.
 *
 * The divisor is the weeks actually covered, not the window's nominal four:
 * a venue connected nine days ago has nine days of sales, and dividing those
 * by four weeks would show every drink at a third of its real rate. It is
 * never less than one week, so the first few nights are reported as the
 * week's units so far rather than multiplied up from a Friday into a figure
 * no Tuesday will match.
 *
 * `specIds` are specs to report even at zero — the ones a till item is
 * mapped to — so a drink that stops selling drops to nought instead of
 * keeping last month's number.
 */
export function posUnitsPerWeek(days, through, specIds = [], windowDays = POS_UNITS_WINDOW_DAYS) {
    return posUnitsIn(days, { from: addDays(through, -(windowDays - 1)), to: through }, specIds);
}
/**
 * The same figure over a window somebody chose (#169), which is what the
 * period picker on screen 07 asks for.
 *
 * Still a weekly rate, not a total: the scatter plots units per week and its
 * quadrants sit at the menu's means, so handing it a month's raw units would
 * move every drink and every boundary with them. A month of trading and a
 * week of it are comparable this way and no other.
 *
 * The divisor runs from the first night with sales to the end of the
 * window, so a venue connected nine days into a month is not reported at a
 * third of its real rate. A window that runs past today is the caller's to
 * clamp — ask for October on the third and the screen passes the third as
 * the end, or every drink reads as a quarter of what it is selling.
 */
export function posUnitsIn(days, window, specIds = []) {
    const within = days.filter((d) => d.serviceDate >= window.from && d.serviceDate <= window.to);
    if (within.length === 0)
        return [];
    const first = within.reduce((min, d) => (d.serviceDate < min ? d.serviceDate : min), within[0].serviceDate);
    // To the end of the window, not to the last night that sold anything: a
    // bar that went quiet for a fortnight has a lower weekly rate, and saying
    // otherwise would report the rate it had while it was busy.
    const spanDays = Math.round((Date.parse(`${window.to}T00:00:00Z`) - Date.parse(`${first}T00:00:00Z`)) / 86_400_000) + 1;
    const weeks = Math.max(1, spanDays / 7);
    const totals = new Map(specIds.map((id) => [id, 0]));
    for (const day of within) {
        for (const [specId, n] of Object.entries(day.units)) {
            totals.set(specId, (totals.get(specId) ?? 0) + n);
        }
    }
    return [...totals.entries()]
        .map(([specId, total]) => ({
        specId,
        unitsPerWeek: Math.max(0, Math.round(total / weeks)),
        periodStart: first,
    }))
        .sort((a, b) => a.specId.localeCompare(b.specId));
}
// -------------------------------------------------------------------- sync
/** How much history the first sync of a newly linked venue reads. */
export const POS_BACKFILL_DAYS = 35;
//# sourceMappingURL=pos.js.map